/**
 * Who is talking.
 *
 * Each audio track is watched locally with a Web Audio analyser rather than
 * asked of the server. mediasoup can report an active speaker, but that names
 * one person per room; what a viewer wants is every tile that is making noise,
 * and the audio is already here to be measured.
 */

/** Loud enough to count as speech, as a fraction of full scale. */
const SPEAKING_LEVEL = 0.045;
/** Quiet enough to stop counting. Lower than the start, so it does not flicker. */
const SILENT_LEVEL = 0.025;
/** How long silence must last before the mark comes off, in ms. */
const HOLD_MS = 500;
/** How often the level is sampled, in ms. */
const SAMPLE_MS = 100;

/**
 * Decides whether someone is speaking, from a running series of levels.
 *
 * Two thresholds rather than one, with the lower used to stop: a single
 * threshold makes the mark blink on and off through the natural dips inside a
 * sentence. The mark then stays on through a pause of up to HOLD_MS, so ordinary
 * gaps between words do not clear it.
 */
export function createSpeechDecider({
  speakingLevel = SPEAKING_LEVEL,
  silentLevel = SILENT_LEVEL,
  holdMs = HOLD_MS,
} = {}) {
  let speaking = false;
  let quietSince = null;

  return function decide(level, now) {
    if (level >= speakingLevel) {
      speaking = true;
      quietSince = null;
    } else if (speaking && level < silentLevel) {
      if (quietSince === null) quietSince = now;
      else if (now - quietSince >= holdMs) {
        speaking = false;
        quietSince = null;
      }
    } else if (speaking) {
      // Between the two thresholds: still talking, just quietly.
      quietSince = null;
    }
    return speaking;
  };
}

/** Root mean square of a byte-domain waveform, as a fraction of full scale. */
export function levelOf(samples) {
  if (!samples || samples.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < samples.length; i += 1) {
    const centred = (samples[i] - 128) / 128;
    sum += centred * centred;
  }
  return Math.sqrt(sum / samples.length);
}

/**
 * Watches audio tracks and reports who is making noise.
 *
 * One AudioContext for all of them - browsers cap how many a page may have - and
 * one analyser per stream, tapped off the stream rather than inserted into
 * playback, so nothing is heard twice.
 */
export class SpeakingMonitor {
  constructor({ onChange, sampleMs = SAMPLE_MS } = {}) {
    this.onChange = onChange ?? (() => {});
    this.sampleMs = sampleMs;
    this.context = null;
    /** key -> { source, analyser, buffer, decide, speaking } */
    this.watched = new Map();
    this.timer = null;
  }

  ensureContext() {
    if (this.context) return this.context;
    const Ctor = globalThis.AudioContext ?? globalThis.webkitAudioContext;
    if (!Ctor) return null;
    this.context = new Ctor();
    return this.context;
  }

  /**
   * Starts watching a stream's audio under `key`. Watching the same key again
   * replaces what was there, which is what happens when a track is swapped.
   */
  watch(key, stream) {
    if (!stream || stream.getAudioTracks().length === 0) {
      this.unwatch(key);
      return;
    }
    const context = this.ensureContext();
    if (!context) return;
    // A context created before any interaction starts suspended; nothing here is
    // audible, so resuming is harmless, and failing to is merely cosmetic.
    if (context.state === 'suspended') context.resume().catch(() => {});

    this.unwatch(key);
    let source;
    try {
      source = context.createMediaStreamSource(stream);
    } catch {
      return; // e.g. a stream whose track ended between the check and here
    }
    const analyser = context.createAnalyser();
    analyser.fftSize = 512;
    // Deliberately not connected to the destination: this only measures.
    source.connect(analyser);

    this.watched.set(key, {
      source,
      analyser,
      buffer: new Uint8Array(analyser.fftSize),
      decide: createSpeechDecider(),
      speaking: false,
    });
    this.start();
  }

  unwatch(key) {
    const entry = this.watched.get(key);
    if (!entry) return;
    try {
      entry.source.disconnect();
      entry.analyser.disconnect();
    } catch {
      /* already gone */
    }
    this.watched.delete(key);
    if (entry.speaking) this.onChange(key, false);
    if (this.watched.size === 0) this.stop();
  }

  start() {
    if (this.timer || this.watched.size === 0) return;
    this.timer = setInterval(() => this.sample(), this.sampleMs);
    if (this.timer.unref) this.timer.unref();
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  sample(now = Date.now()) {
    for (const [key, entry] of this.watched) {
      entry.analyser.getByteTimeDomainData(entry.buffer);
      const speaking = entry.decide(levelOf(entry.buffer), now);
      if (speaking !== entry.speaking) {
        entry.speaking = speaking;
        this.onChange(key, speaking);
      }
    }
  }

  /** Drops every watcher and releases the audio context. */
  close() {
    for (const key of [...this.watched.keys()]) this.unwatch(key);
    this.stop();
    if (this.context) {
      this.context.close().catch(() => {});
      this.context = null;
    }
  }
}

export default { SpeakingMonitor, createSpeechDecider, levelOf };
